/**
 * linkPropertyAsCandidate — THE ONE linking path (2026-06-28 unification, Gap #2).
 *
 * Before this, two divergent link models disagreed:
 *   · the project-side `linkPropertyToProject` wrote BOTH `project.propertyRefs[]`
 *     AND `property.engagementRefs[]`, but stamped no candidate;
 *   · the intake-slot `PropertySlotLink` wrote `engagementRefs` (or neither) but
 *     NOT `propertyRefs` → "linked but not showing" in the Linked-Properties panel.
 *
 * This helper collapses every link entry point to ONE consistent result:
 *   (a) ONE bidirectional link via `linkPropertyToProject` (now idempotent — it
 *       reads both sides and writes only the missing one, so re-linking never
 *       appends a duplicate ref despite the timestamp; `arrayUnion` still
 *       preserves OTHER projects' refs → multi-project reuse is untouched);
 *   (b) for the L1A candidate model, ensures a candidate slot pre-stamped with the
 *       propertyId exists in the project intake — preferring to ABSORB an existing
 *       unjoined candidate (the staff auto-join) before appending a thin new one.
 *
 * Idempotent: re-linking the same property writes no duplicate ref and no
 * duplicate candidate. PropertySlotLink passes `ensureCandidate: false` — the
 * intake slot it renders IS the candidate (stamped via its own `onPatchSlot`), so
 * appending here would double it; that path only needs side (a).
 *
 * Consumes existing helpers only (linkPropertyToProject, loadPropertiesIntake,
 * saveProperties, candidateForPropertyRef, joinCandidatesToLinkedProperties) — no
 * new write path.
 */
import { linkPropertyToProject } from './projectStore';
import {
  loadPropertiesIntake,
  saveProperties,
  candidateForPropertyRef,
  type PropertyInput,
} from './staffIntake';
import {
  joinCandidatesToLinkedProperties,
  type PropertyRefLike,
} from './candidatePropertyJoin';

export interface LinkPropertyAsCandidateOptions {
  /**
   * When true (default), ensure a candidate slot pre-stamped with this property
   * exists in the project intake. Pass FALSE from PropertySlotLink (its rendered
   * intake slot already IS the candidate, stamped via `onPatchSlot`) and from
   * non-L1A tiers (which have no candidate-grading model).
   */
  ensureCandidate?: boolean;
  /**
   * The project's current `propertyRefs[]` (when the caller has them in state) so
   * the candidate auto-join's ambiguity rules match the linking panel's. The
   * target propertyId is added automatically if absent.
   */
  refs?: PropertyRefLike[];
}

export interface LinkPropertyAsCandidateResult {
  propertyId: string;
  /** a thin new candidate slot was appended (no existing one could take it). */
  candidateAdded: boolean;
  /** an existing unjoined candidate was stamped with this propertyId instead. */
  candidateJoined: boolean;
}

export interface CandidateSlotPlan {
  /** the candidates array to persist (unchanged when nothing to do). */
  next: PropertyInput[];
  /** the propertyId was already on some candidate → no write needed. */
  alreadyBound: boolean;
  /** an existing unjoined candidate was stamped with this propertyId. */
  candidateJoined: boolean;
  /** a thin new candidate slot was appended. */
  candidateAdded: boolean;
  /** true when `next` differs from the input (i.e. a save is needed). */
  changed: boolean;
}

/**
 * PURE — decide the candidate slots for a link (the testable core of side (b)).
 * Idempotent: if a candidate already carries `propertyId`, returns the input
 * unchanged. Otherwise tries to ABSORB an unjoined candidate (the staff auto-join,
 * using the full ref set so ambiguity rules match the panel); failing that,
 * APPENDS a thin pre-stamped candidate. Touches only THIS project's candidates →
 * never disturbs another project's intake (multi-project reuse).
 */
export function planCandidateForLink(
  candidates: PropertyInput[],
  refs: PropertyRefLike[],
  propertyId: string,
): CandidateSlotPlan {
  const cands = Array.isArray(candidates) ? candidates : [];

  if (cands.some((c) => c.propertyId === propertyId)) {
    return { next: cands, alreadyBound: true, candidateJoined: false, candidateAdded: false, changed: false };
  }

  const allRefs: PropertyRefLike[] = Array.isArray(refs) ? refs.slice() : [];
  if (!allRefs.some((r) => r.propertyId === propertyId)) allRefs.push({ propertyId });

  const { joined } = joinCandidatesToLinkedProperties(cands, allRefs);
  const bound = joined.some((c) => c.propertyId === propertyId);

  if (bound) {
    return { next: joined, alreadyBound: false, candidateJoined: true, candidateAdded: false, changed: true };
  }
  return {
    next: [...joined, candidateForPropertyRef(propertyId)],
    alreadyBound: false,
    candidateJoined: false,
    candidateAdded: true,
    changed: true,
  };
}

/**
 * Link a property to a project AND (for L1A) ensure a gradeable candidate exists.
 * The single entry point every link UI routes through.
 */
export async function linkPropertyAsCandidate(
  projectId: string,
  clientId: string,
  propertyId: string,
  role: 'primary' | 'candidate',
  intakeTier: string,
  opts: LinkPropertyAsCandidateOptions = {},
): Promise<LinkPropertyAsCandidateResult> {
  // (a) ONE bidirectional link — idempotent (propertyRefs + engagementRefs once).
  await linkPropertyToProject(projectId, propertyId, role);

  // PropertySlotLink / non-L1A: side (a) only — the slot is already the candidate.
  if (opts.ensureCandidate === false || !clientId || !intakeTier) {
    return { propertyId, candidateAdded: false, candidateJoined: false };
  }

  // (b) ensure a candidate slot pre-stamped with this property exists in the intake.
  const scope = { clientId, projectId };
  const { form, recommendedPropertyId } = await loadPropertiesIntake(scope, intakeTier);

  const plan = planCandidateForLink(form.properties, opts.refs ?? [], propertyId);

  // idempotent — nothing to write when the candidate already exists.
  if (!plan.changed) {
    return { propertyId, candidateAdded: false, candidateJoined: false };
  }

  await saveProperties(
    scope,
    { north_rotation: form.north_rotation, properties: plan.next },
    { uid: null, via: 'staff' },
    { recommendedPropertyId: recommendedPropertyId || '' },
    intakeTier,
  );

  return { propertyId, candidateAdded: plan.candidateAdded, candidateJoined: plan.candidateJoined };
}
